import { forwardRef, useEffect } from 'react';

import dayjs from 'dayjs';

import { UPDATE_PERIOD } from '@/utils/hooks/datepicker/actions';
import useDateLimits from '@/utils/hooks/datepicker/useDateLimits';
import { useDatepicker } from '@/utils/hooks/datepicker/useDatepicker';
// Hooks
import useUser from '@/utils/hooks/swr/useUser';

import PropTypes from 'prop-types';

import datepickerDateFormat from '@/lib/datepicker/dateFormat';
// Lib
import { ALL_TIME, CUSTOM } from '@/lib/periods';

// Components
import Datepicker from '@/components/base/Datepicker';
import DatepickerInput from '@/components/base/Datepicker/DatepickerInput';

const DatepickerInputWrapper = forwardRef(({ period, ...restProps }, ref) => {
    const { dispatch } = useDatepicker();
    useEffect(() => {
        dispatch({
            type: UPDATE_PERIOD,
            payload: period
        });
    }, [period]);

    return <DatepickerInput {...restProps} ref={ref} />;
});

DatepickerInputWrapper.displayName = 'DatepickerInput';

DatepickerInputWrapper.Proptypes = {
    periodSelected: PropTypes.object.isRequired,
    statePeriodSelected: PropTypes.object.isRequired
};

const DatepickerMediaLibrary = ({
    periodSelected,
    setRequestParams = () => {
        /** */
    }
}) => {
    const { maxDate } = useDateLimits();
    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const setDatePickerDate = ({ periodSelected: period, date }) => {
        if (!period || !date || period === ALL_TIME) {
            setRequestParams((prevState) => ({ ...prevState, added_at: undefined }));
            return;
        }

        const added_at = { period };

        if (added_at.period === CUSTOM) {
            added_at.start_date = dayjs(date[0]).format('YYYY-MM-DD');
            added_at.end_date = date.length > 1 ? dayjs(date[1]).format('YYYY-MM-DD') : undefined;
        }

        setRequestParams((prevState) => ({ ...prevState, added_at }));
    };

    return (
        <Datepicker
            periods="medias"
            periodSelected={periodSelected}
            timezone={user?.settings.timezone}
            onChange={setDatePickerDate}
            className="!z-10"
            options={{
                placement: 'top-end',
                dateFormat: datepickerDateFormat[user?.settings.date_format] ?? 'Y/m/d',
                mode: 'range',
                parseDate: (dateStr) => dayjs(dateStr).toDate(),
                maxDate,
                minDate: user?.created_at
            }}>
            <DatepickerInputWrapper noBorder={false} isLeftIcon={false} period={periodSelected} />
        </Datepicker>
    );
};

DatepickerMediaLibrary.propTypes = {
    periodSelected: PropTypes.string,
    setRequestParams: PropTypes.func
};

export default DatepickerMediaLibrary;
